import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover'
import {
  BookOpen,
  Bookmark,
  Check,
  CheckCheck,
  ChevronDown,
  Folders,
  type LucideIcon,
  Star,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { cn } from '@/utils'
import { usePhotoAlbumsQuery } from '@/api/hooks/use-photo-albums'
import {
  CURATED_ALBUM_OPTIONS,
  type CuratedAlbumIcon,
  curatedAlbumRequiresSpreadId,
  decodeAlbumSelection,
  encodeAlbumSelection,
  getAlbumSelectionLabel,
} from '@/lib/photos/album-selection'
import { filterAlbumsBySearch } from '@/lib/photos/album-search'

const iconMap = {
  folders: Folders,
  star: Star,
  'book-open': BookOpen,
  bookmark: Bookmark,
  'check-check': CheckCheck,
}

/** One album option: optional curated icon, label, and a check mark when it is the selection. */
function AlbumCommandItem({
  value,
  label,
  icon: Icon,
  selected,
  onSelect,
}: {
  value: string
  label: string
  icon?: LucideIcon
  selected: boolean
  onSelect: (value: string) => void
}) {
  return (
    <CommandItem value={label} onSelect={() => onSelect(value)}>
      {Icon ? <Icon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" /> : null}
      <span className="flex-1 text-left">{label}</span>
      <Check className={cn('h-4 w-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
    </CommandItem>
  )
}

export type SearchAlbumsComboboxProps = {
  value: string | null
  onChange: (value: string | null) => void
  spreadId?: string | null
  placeholder?: string
  onOpenChange?: (open: boolean) => void
}

export function SearchAlbumsCombobox({
  value,
  onChange,
  spreadId,
  placeholder = 'Select album',
  onOpenChange,
}: SearchAlbumsComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const {
    data: jobAlbums = [],
    isLoading,
    isError,
    error,
  } = usePhotoAlbumsQuery({
    enabled: open || value != null,
  })

  const selection = decodeAlbumSelection(value, jobAlbums)
  const triggerLabel = getAlbumSelectionLabel(selection) ?? placeholder

  const curatedItems = useMemo(
    () =>
      CURATED_ALBUM_OPTIONS.filter(
        (option) => !curatedAlbumRequiresSpreadId(option.key) || Boolean(spreadId)
      ).map((option) => ({
        value: encodeAlbumSelection({ type: 'curated', key: option.key }),
        label: option.label,
        icon: option.icon,
      })),
    [spreadId]
  )

  const jobItems = useMemo(
    () =>
      jobAlbums.map((album) => ({
        value: encodeAlbumSelection({
          type: 'album',
          albumId: album.albumId,
          displayName: album.displayName,
        }),
        label: album.displayName,
      })),
    [jobAlbums]
  )

  const filteredCurated = useMemo(() => {
    const items = curatedItems.map((item) => ({ ...item, displayName: item.label }))

    return filterAlbumsBySearch(items, search, { isCurated: true }) as typeof items
  }, [curatedItems, search])

  const filteredJobAlbums = useMemo(() => {
    const items = jobItems.map((item) => ({ ...item, displayName: item.label }))

    return filterAlbumsBySearch(items, search) as typeof items
  }, [jobItems, search])

  const hasVisibleResults = filteredCurated.length > 0 || filteredJobAlbums.length > 0

  function handleSelect(nextValue: string) {
    onChange(nextValue)
    setSearch('')
    setOpen(false)
    onOpenChange?.(false)
  }

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
    onOpenChange?.(nextOpen)
    if (!nextOpen) setSearch('')
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-2">
      <span className="text-sm font-medium leading-none text-muted-foreground">Album</span>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label={`Album: ${triggerLabel}`}
            className="flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-left shadow-xs outline-none transition-[color,box-shadow] hover:bg-accent/30 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-medium leading-5 text-foreground">
              {triggerLabel}
            </span>
            <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search albums"
              value={search}
              onValueChange={setSearch}
              autoFocus
            />
            <CommandList>
              {isLoading && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading albums…
                </div>
              )}
              {isError && !isLoading && (
                <div className="py-6 px-3 text-center text-sm text-destructive">
                  {error instanceof Error ? error.message : 'Failed to load albums'}
                </div>
              )}
              {!isLoading && !isError && (
                <>
                  {!hasVisibleResults && <CommandEmpty>No results found.</CommandEmpty>}
                  {filteredCurated.length > 0 && (
                    <CommandGroup>
                      {filteredCurated.map((item) => {
                        const curated = item as (typeof curatedItems)[number] & {
                          displayName: string
                        }

                        return (
                          <AlbumCommandItem
                            key={curated.value}
                            value={curated.value}
                            label={curated.label}
                            icon={iconMap[curated.icon as CuratedAlbumIcon]}
                            selected={value === curated.value}
                            onSelect={handleSelect}
                          />
                        )
                      })}
                    </CommandGroup>
                  )}
                  {filteredCurated.length > 0 && filteredJobAlbums.length > 0 && (
                    <div className="h-px bg-border my-1" role="separator" />
                  )}
                  {filteredJobAlbums.length > 0 && (
                    <CommandGroup>
                      {filteredJobAlbums.map((item) => (
                        <AlbumCommandItem
                          key={item.value}
                          value={item.value}
                          label={item.label}
                          selected={value === item.value}
                          onSelect={handleSelect}
                        />
                      ))}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
